import * as Sentry from '@sentry/browser';
import type { SagaIterator } from 'redux-saga';
import type { Socket } from 'socket.io-client';
import { call, put, race, take, delay } from 'redux-saga/effects';

import { setMessageStatus, receiveNewMessage, triggerChatSocket } from '../../Redux/ChatListActions';
import { getResultChannel } from './Channels';
import createUuid from '../../Utils/createUuid';
import type { ChatroomType, PostAttachmentData } from '../../Utils/types';
import hasValue from '../../Utils/hasValue';
import devLog from '../../Utils/logger';

export type ChatCommand =
  | 'get-application-data'
  | 'send-message-v2'
  | 'read-messages'
  | 'clear-notifications'
  | 'add-user-v2'
  | 'resend-notification'
  | 'remove-member-v2'
  | 'create-chatroom'
  | 'update-chatroom'
  | 'update-room-icon'
  | 'ack-message'
  | 'start-typing'
  | 'stop-typing'
  | 'pin-chatroom'
  | 'unpin-chatroom'
  | 'pin-message'
  | 'unpin-message'
  | 'redact-message'
  | 'unredact-message'
  | 'edit-message'
  | 'delete-chatroom'
  | 'add-multiple-users'
  | 'delete-file-message';

export type CaseCommand = 'pin-case' | 'unpin-case';

export type SendCommand = {
  type: 'SOCKET/COMMAND';
  transactionId: string;
  chatId?: number;
  caseId?: number;
  command: ChatCommand | CaseCommand;
  params: Array<any>;
};

export type CommandResult = {
  type: 'SOCKET/RESPONSE';
  transactionId: string;
  result: any;
  error: any;
};

export type CommandMethod = (
  socket: typeof Socket,
  chatId: number,
  memberId: number,
  transactionId: string,
  ...params: Array<any>
) => void;

export const sendCommand = (
  chatId: number | undefined,
  userId: number,
  command: ChatCommand,
  params: Array<any>,
): SendCommand => ({
  type: 'SOCKET/COMMAND',
  transactionId: createUuid(userId),
  chatId,
  command,
  params,
});

export const sendCaseCommand = (
  caseId: number,
  userId: number,
  command: CaseCommand,
  params: Array<any>,
): SendCommand => ({
  type: 'SOCKET/COMMAND',
  transactionId: createUuid(userId),
  caseId,
  command,
  params,
});

export const commandResult = (transactionId: string, result: any, error: any): CommandResult => ({
  type: 'SOCKET/RESPONSE',
  transactionId,
  result,
  error,
});

type GetApplicationDataPayload = {
  uuid_type: string;
  transaction_uuid: string;
  get_organizations: boolean;
  get_chatrooms: boolean;
  get_instant_messages: boolean;
  get_chatroom_members: boolean;
  chatroom_member_chatrooms: Array<number>;
  instant_message_chatrooms: Array<number>;
};

const getApplicationData = (
  socket: typeof Socket,
  _chatId: number,
  memberId: number,
  transactionId: string,
  requestData: any,
): void => {
  const payload: GetApplicationDataPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    get_organizations: requestData.getOrganizations,
    get_chatrooms: requestData.getChatrooms,
    get_instant_messages: requestData.getInstantMessages,
    get_chatroom_members: requestData.getChatroomMembers,
    chatroom_member_chatrooms: requestData.chatroomIds,
    instant_message_chatrooms: requestData.chatroomIds,
  };

  socket.emit('get-application-data', payload);
};

type WriteMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  text_content: string;
  chat_type_id: number;
  mentioned_users: Array<number>;
  attachments: Array<PostAttachmentData>;
  replying_to?: number | null;
  case_id?: number | null;
  high_priority?: boolean | null;
};

const writeMessage = (
  socket: typeof Socket,
  chatId: number,
  memberId: number,
  transactionId: string,
  textContent: string,
  messageType: number,
  mentionedUsers: Array<number>,
  attachments: Array<PostAttachmentData>,
  replyMessageId?: number | null,
  caseId?: number | null,
  highPriority?: boolean | null,
): void => {
  const payload: WriteMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    text_content: textContent,
    chat_type_id: messageType,
    mentioned_users: mentionedUsers,
    attachments,
    replying_to: replyMessageId,
    case_id: caseId,
    high_priority: highPriority,
  };
  socket.emit('send-message-v2', payload);
};

type ReadMessagesPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id_list: Array<number>;
};

const readMessages = (
  socket: typeof Socket,
  chatId: number,
  memberId: number,
  transactionId: string,
  messageIds: Array<number>,
): void => {
  const payload: ReadMessagesPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id_list: messageIds,
  };

  socket.emit('read-messages', payload);
};

type ClearNotificationsPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const clearNotifications = (socket: typeof Socket, chatId: number, memberId: number, transactionId: string): void => {
  const payload: ClearNotificationsPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('clear-notifications', payload);
};

type CreateChatroomBasePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_type: ChatroomType;
  managed: boolean;
};

type CreateChatroomStandardPayload = CreateChatroomBasePayload & {
  chatroom_name: string;
  chatroom_description?: string;
};

type CreateChatroomCasePayload = CreateChatroomBasePayload & {
  chatroom_name: string;
  chatroom_description?: string;
  donor_id: string | null | undefined;
  organ_id: string | null | undefined;
};

type CreateChatroomTemplatedCasePayload = CreateChatroomBasePayload & {
  donor_id: string | null | undefined;
  organ_id: string | null | undefined;
  transplant_center: number | null | undefined;
};

type CreateChatroomDonorOnlyPayload = CreateChatroomBasePayload & {
  chatroom_name: string;
  chatroom_description?: string;
  donor_id: string | null | undefined;
  restrictions?: boolean | null;
};

type CreateChatroomPayload =
  | CreateChatroomStandardPayload
  | CreateChatroomCasePayload
  | CreateChatroomTemplatedCasePayload
  | CreateChatroomDonorOnlyPayload;

const createChatroom = (
  socket: typeof Socket,
  _chatId: number,
  _memberId: number,
  transactionId: string,
  name: string,
  description: string | null | undefined,
  donorId: string | null | undefined,
  organId: string | null | undefined,
  organization: number | null | undefined,
  chatroomType: ChatroomType,
  managed: boolean,
  restrictions: boolean | null | undefined,
): void => {
  let payload: CreateChatroomPayload;

  if (chatroomType === 'Standard' || chatroomType === 'Research Offer') {
    payload = {
      uuid_type: 'v1',
      transaction_uuid: transactionId,
      chatroom_name: name,
      chatroom_description: description || undefined,
      chatroom_type: chatroomType,
      managed,
    };
  } else if (chatroomType === 'Case' || chatroomType === 'Donor') {
    payload = {
      uuid_type: 'v1',
      transaction_uuid: transactionId,
      chatroom_name: name,
      chatroom_description: description || undefined,
      donor_id: donorId,
      organ_id: organId,
      chatroom_type: chatroomType,
      managed,
    };
  } else if (chatroomType === 'Templated Case') {
    payload = {
      uuid_type: 'v1',
      transaction_uuid: transactionId,
      donor_id: donorId,
      organ_id: organId,
      transplant_center: organization,
      chatroom_type: chatroomType,
      managed,
    };
  } else if (chatroomType === 'Donor Only') {
    payload = {
      uuid_type: 'v1',
      transaction_uuid: transactionId,
      chatroom_name: name,
      chatroom_description: description || undefined,
      donor_id: donorId,
      chatroom_type: chatroomType,
      managed: managed || false,
      restrictions,
    };
  } else {
    throw new Error(`Unsupported chatroom type: ${chatroomType}`);
  }

  socket.emit('create-chatroom', payload);
};

type AddUserPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  email?: string;
  phone?: string;
};

const addUser = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  email: string,
  phone: string,
): void => {
  const payload: AddUserPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  if (phone) {
    payload.phone = phone;
  } else {
    payload.email = email;
  }
  socket.emit('add-user-v2', payload);
};

type AddMultipleUsersPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  usersAdded: any[];
};

const addMultipleUsers = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  usersAdded: any[],
): void => {
  const payload: AddMultipleUsersPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    usersAdded,
  };

  socket.emit('add-multiple-users', payload);
};

type RemoveMemberPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  email: string;
};

const removeMember = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  email: string,
): void => {
  const payload: RemoveMemberPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    email,
  };

  socket.emit('remove-member-v2', payload);
};

type DeleteChatroomPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const deleteChatroom = (socket: typeof Socket, chatId: number, _memberId: number, transactionId: string): void => {
  const payload: DeleteChatroomPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('delete-chatroom', payload);
};

type UpdateChatroomPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  managed: boolean;
  chatroom_description?: string;
  chatroom_name?: string;
};
const updateChatroom = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  description: string,
  name: string,
  managed: boolean,
): void => {
  const payload: UpdateChatroomPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    managed: managed,
  };

  if (hasValue(description)) {
    payload.chatroom_description = description;
  }

  if (hasValue(name)) {
    payload.chatroom_name = name;
  }

  socket.emit('update-chatroom', payload);
};

type UpdateRoomIconPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  room_icon: string | null;
};

const updateRoomIcon = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  roomIcon: string | null,
): void => {
  const payload: UpdateRoomIconPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    room_icon: roomIcon,
  };

  socket.emit('update-room-icon', payload);
};

type AckMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
  symbol: string;
};

const ackMessage = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  messageId: number,
  symbol: string,
): void => {
  const payload: AckMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
    symbol,
  };

  socket.emit('ack-message', payload);
};

type StartTypingPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const startTyping = (socket: typeof Socket, chatId: number, _memberId: number, transactionId: string): void => {
  const payload: StartTypingPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('start-typing', payload);
};

type StopTypingPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const stopTyping = (socket: typeof Socket, chatId: number, _memberId: number, transactionId: string): void => {
  const payload: StopTypingPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('stop-typing', payload);
};

type PinChatroomPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const pinChatroom = (socket: typeof Socket, chatId: number, _memberId: number, transactionId: string): void => {
  const payload: PinChatroomPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('pin-chatroom', payload);
};

type UnpinChatroomPayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
};

const unpinChatroom = (socket: typeof Socket, chatId: number, _memberId: number, transactionId: string): void => {
  const payload: UnpinChatroomPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
  };

  socket.emit('unpin-chatroom', payload);
};

type PinMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
};

const pinMessage = (
  socket: typeof Socket,
  chatId: number,
  memberId: number,
  transactionId: string,
  messageId: number,
): void => {
  const payload: PinMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
  };

  socket.emit('pin-message', payload);
};

type UnpinMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
};

const unpinMessage = (
  socket: typeof Socket,
  chatId: number,
  memberId: number,
  transactionId: string,
  messageId: number,
): void => {
  const payload: UnpinMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
  };

  socket.emit('unpin-message', payload);
};

type PinCasePayload = {
  uuid_type: string;
  transaction_uuid: string;
  case_id: number;
};

const pinCase = (socket: typeof Socket, caseId: number, _memberId: number, transactionId: string): void => {
  const payload: PinCasePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    case_id: caseId,
  };

  socket.emit('pin-case', payload);
};

type UnpinCasePayload = {
  uuid_type: string;
  transaction_uuid: string;
  case_id: number;
};

const unpinCase = (socket: typeof Socket, caseId: number, _memberId: number, transactionId: string): void => {
  const payload: UnpinCasePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    case_id: caseId,
  };

  socket.emit('unpin-case', payload);
};

type RedactMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
};

const redactMessage = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  messageId: number,
): void => {
  const payload: RedactMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
  };

  socket.emit('redact-message', payload);
};

type UnredactMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
};

const unredactMessage = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  messageId: number,
): void => {
  const payload: UnredactMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
  };

  socket.emit('unredact-message', payload);
};

type EditMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
  text_content: string;
};

const editMessage = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  messageId: number,
  textContent: string,
): void => {
  const payload: EditMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
    text_content: textContent,
  };

  socket.emit('edit-message', payload);
};

type DeleteFileMessagePayload = {
  uuid_type: string;
  transaction_uuid: string;
  chatroom_id: number;
  instant_message_id: number;
};

const deleteFileMessage = (
  socket: typeof Socket,
  chatId: number,
  _memberId: number,
  transactionId: string,
  messageId: number,
): void => {
  const payload: DeleteFileMessagePayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    chatroom_id: chatId,
    instant_message_id: messageId,
  };

  socket.emit('delete-file-message', payload);
};

type ResendNotificationPayload = {
  uuid_type: string;
  transaction_uuid: string;
  user_id: number;
  instant_message_id: number;
};

const resendNotification = (
  socket: typeof Socket,
  _chatId: number,
  _memberId: number,
  transactionId: string,
  userId: number,
  messageId: number,
): void => {
  const payload: ResendNotificationPayload = {
    uuid_type: 'v1',
    transaction_uuid: transactionId,
    user_id: userId,
    instant_message_id: messageId,
  };
  socket.emit('resend-notification', payload);
};

export function* sendMessage(
  chatId: number,
  memberId: number,
  textContent: string,
  messageType: number,
  tmpId: string,
  attachments: Array<PostAttachmentData>,
  mentionedUsers: Array<number>,
  replyMessageId: number | undefined,
  caseId: number | undefined,
  highPriority: boolean | undefined,
): SagaIterator<void> {
  const ack: CommandResult | undefined = yield call(sendCommandAndGetResult, chatId, memberId, 'send-message-v2', [
    textContent,
    messageType,
    mentionedUsers,
    attachments,
    replyMessageId,
    caseId,
    highPriority,
  ]);

  if (ack && ack.result) {
    // Success, lets update the local chat item
    for (let i = 0; i < ack.result.instant_messages.length; i += 1) {
      const instantMessage = ack.result.instant_messages[i];
      yield put(
        receiveNewMessage(
          chatId,
          memberId,
          {
            id: instantMessage.instant_message_id,
            senderId: memberId,
            senderName: instantMessage.sender_name,
            sentTime: instantMessage.sent_time,
            messageType: instantMessage.chat_type_id,
            textContent: instantMessage.text_content,
            dataContent: null,
            displayName: instantMessage.display_name,
            fileName: instantMessage.file_name,
            presignedUrl: instantMessage.presigned_url,
            presignedThumbnailUrl: instantMessage.presigned_thumbnail_url,
            localPath: undefined,

            seenByCount: 0,
            seenByCurrentUser: true,

            status: undefined,
            ack: {},
            redactTime: undefined,
            editTime: undefined,
            replyingMessageId: instantMessage.replying_to,
            replyingTextContent: instantMessage.replying_to_text_content,
            replyingMessageType: instantMessage.replying_to_chat_type_id,
            replyingDataContent: instantMessage.replying_to_data_content,
            replyingFilePath: instantMessage.replying_to_file_path,
            replyingSenderId: instantMessage.replying_to_sender_id,
            parentMessageId: instantMessage.parent_message_id,
            caseId: instantMessage.case_id,
            highPriority: instantMessage.high_priority || false,
          },
          instantMessage.instant_message_id,
          'end',
          {
            textContent: instantMessage.text_content ? instantMessage.text_content : 'File uploaded',
            sentTime: instantMessage.sent_time,
          },
          instantMessage.sent_time,
          1,
        ),
      );
    }
  } else {
    // Set the status of the message to failed
    yield put(setMessageStatus(chatId, tmpId, 'failed'));
  }
}

function* waitForCommandResult(action: SendCommand): SagaIterator<CommandResult | null | undefined> {
  const channel = getResultChannel();

  if (channel) {
    // wait for correct result
    while (true) {
      const ack: CommandResult = yield take(channel);

      if (ack.transactionId === action.transactionId) {
        // Attach original command to the error so we can log better errors to sentry
        // TODO: Shouldn't this be handled higher up?
        if (ack.error && !ack.error.requestSocketEvent) {
          ack.error.requestSocketEvent = action.command;

          Sentry.captureException(ack.error, { extra: { errorMsg: 'waitForCommandResult Failed', ack } });
        }
        return ack;
      }

      // If this is not the result we are looking for put it back into the result channel
      devLog('socket-stress', ['waitForCommandResult: System under stress : responses are overlapping ...', ack]);
      yield put(ack);

      // Release cpu resources a bit
      // TODO: Research alternative improvements to this forced delay...
      yield delay(32);
    }
  } else if (import.meta.env.MODE === 'development') {
    console.warn('Failed to get command result channel');
  }

  return undefined;
}

export function* sendCommandAndGetResult(
  chatId: number | undefined,
  memberId: number,
  command: ChatCommand,
  params: Array<any>,
): SagaIterator<CommandResult | undefined> {
  const cmd = sendCommand(chatId, memberId, command, params);

  yield put(cmd);

  // Increased the timeout on the race condition here to a larger size to help lower connectivity clients
  // and a potentially larger data size being returned, particularly with get-application-data
  // There is also a "heartbeat" ping interval that is configured on the server in socket_service.ts
  // that is used to check if the client is still connected to the server. That heartbeat interval and timeout
  // should be sufficiently high that it doesn't cause the socket to disconnect before the command timeout occurs
  const raceResult = yield race({
    commandResult: call(waitForCommandResult, cmd),
    timeout: delay(30000),
  });

  if (raceResult.commandResult) {
    return raceResult.commandResult;
  }

  yield put(triggerChatSocket());

  return commandResult(cmd.transactionId, undefined, {
    error: 'Timed out',
    // Attach original command to the error so we can log better errors to sentry
    requestSocketEvent: command,
  });
}

export function* sendCommandNoResult(
  chatId: number,
  memberId: number,
  command: ChatCommand,
  params: Array<any>,
): SagaIterator<void> {
  const cmd = sendCommand(chatId, memberId, command, params);
  devLog('queue', ['sendCommandNoResult', cmd]);

  yield put(cmd);
}

export function* sendCaseCommandNoResult(
  caseId: number,
  memberId: number,
  command: CaseCommand,
  params: Array<any>,
): SagaIterator<void> {
  const cmd = sendCaseCommand(caseId, memberId, command, params);
  devLog('queue', ['sendCaseCommandNoResult', cmd]);

  yield put(cmd);
}

export const getCommandMethod = (command: string): CommandMethod | null | undefined => {
  // Map the command to an actual method
  switch (command || '') {
    case 'get-application-data':
      return getApplicationData;

    case 'send-message-v2':
      return writeMessage;

    case 'read-messages':
      return readMessages;

    case 'clear-notifications':
      return clearNotifications;

    case 'add-user-v2':
      return addUser;

    case 'add-multiple-users':
      return addMultipleUsers;

    case 'remove-member-v2':
      return removeMember;

    case 'create-chatroom':
      return createChatroom;

    case 'update-chatroom':
      return updateChatroom;

    case 'update-room-icon':
      return updateRoomIcon;

    case 'ack-message':
      return ackMessage;

    case 'start-typing':
      return startTyping;

    case 'stop-typing':
      return stopTyping;

    case 'pin-chatroom':
      return pinChatroom;

    case 'unpin-chatroom':
      return unpinChatroom;

    case 'pin-message':
      return pinMessage;

    case 'unpin-message':
      return unpinMessage;

    case 'redact-message':
      return redactMessage;

    case 'unredact-message':
      return unredactMessage;

    case 'edit-message':
      return editMessage;

    case 'delete-file-message':
      return deleteFileMessage;

    case 'resend-notification':
      return resendNotification;

    case 'delete-chatroom':
      return deleteChatroom;

    case 'pin-case':
      return pinCase;

    case 'unpin-case':
      return unpinCase;

    default:
      break;
  }

  return undefined;
};
